import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import MultiOptionCheckbox from "../../../ui/MultiOptionCheckbox";
import FilteredDropdown from "../../../ui/FilterDropdown";
import ApiResponse from "../../../types/IApiResponse";
import { fetchData, postData } from "../../../services/api";
import handleApiResponse from "../../../utils/handleApiResponse";
import apiUrls from "../../../config/apiUrls";
import SquadPlayerData from "./SquadPlayersData";
import { FilterSectionStyles, SortDropdown } from "../../../styled-components/FilterStyles";
import { IRegionAreaModel } from "../../../types/IRegionAreaData";
import { useLoading } from "../../../contexts/LoadingContext";
import FilterSort from "../../PlayerLanding/PlayerSearch/FilterSort";
import SecondaryButton from "../../../ui/SecondaryButton";
import LabelText from "../../../ui/LabelText";
import { IPlayerCardDetail } from "../../../types/IPlayerCardDetail";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import IApiResponse from "../../../types/IApiResponse";
import { useNavigate } from "react-router-dom";
import { ModuleSpinner } from "../../../ui/LoadingSpinner/ModuleSpinner";
import LoadingSpinner from "../../../ui/LoadingSpinner";

const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  padding-bottom: 16px;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
  .contentText {
    color: ${({ theme }) => theme.playerColor};
  }
`;

const StyledFiltersData = styled.div`
  display: flex;
  gap: 36px;
  flex-direction: column;
  background: ${({ theme }) => theme.playerContentBackground};
`;

interface ISquadPlayersProps {
  onSelectTeam?: (teamId: string) => void;
  teamId?: string;
}

interface SelectedOption {
  region: string;
  optionId: number;
}

const SquadPlayers: React.FC<ISquadPlayersProps> = ({ onSelectTeam, teamId }) => {
  const { startLoading, stopLoading, loading } = useLoading();
  const { sortSquadOrderName, sortSquadOrdering, regionAreaData } = useClubSearch();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<string[]>(["GK", "Defence", "Midfielder", "Attack"]);
  const [status, setStatus] = useState<string[]>([]);
  const [region, setRegion] = useState<SelectedOption[]>([]);
  const [isSortClicked, setIsSortClicked] = useState(false);
  const [squadPlayersData, setSquadPlayersData] = useState<IPlayerCardDetail[]>([]);
  const [filteredPlayersData, setFilteredPlayersData] = useState<IPlayerCardDetail[]>([]);
  const numberOfFitPlayers = squadPlayersData.filter((player) => player.isInjured === false);
  const numberOfInjuredPlayers = squadPlayersData.filter((player) => player.isInjured === true);
  const numberOfLoanedInPlayers = squadPlayersData.filter((player) => player.onLoan === true);
  const numberOfLoanedOutPlayers = squadPlayersData.filter((player) => player.givenOnLoan === true);
  const [regionData, setRegionData] = useState<IRegionAreaModel[] | undefined>();
  const [isSquadDataLoading , setIsSquadDataLoading] = useState<boolean>(false);
  const regions = regionAreaData === undefined || regionAreaData === null ? regionData : regionAreaData;
  const filterSortRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterSortRef.current && !filterSortRef.current.contains(event.target as Node)) {
        setIsSortClicked(false); // Close FilterSort if clicked outside
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterSortRef]);

  const fetchAllAreaByRegions = async () => {
    const response: IApiResponse<any> = await fetchData(`${apiUrls["GetAllAreaByRegions"]}`);
    handleApiResponse(
      response,
      (regionareaData) => {
        setRegionData(regionareaData);
      },
      (error) => {
        console.error("Error fetching Home Team Data:", error);
      }
    );
  };

  useEffect(() => {
    if (regionAreaData === undefined) {
      fetchAllAreaByRegions();
    }
  }, []);

  const handleSelectionChange = async (selectedOptions: string[]) => {
    startLoading();
    setRoles(selectedOptions);
    await new Promise((resolve) => setTimeout(resolve, 500));
    stopLoading();
    await new Promise((resolve) => setTimeout(resolve, 500));
    stopLoading();
  };

  const handleStatusSelectionChange = async (selectedOptions: string[]) => {
    startLoading();
    setStatus(selectedOptions);
    await new Promise((resolve) => setTimeout(resolve, 500));
    stopLoading();
    await new Promise((resolve) => setTimeout(resolve, 500));
    stopLoading();
  };

  useEffect(() => {
    const fetchTeamSquadPlayerCardData = async () => {
      setIsSquadDataLoading(true);
      const requestParameters = {
        teamId: teamId,
        OrderName: sortSquadOrderName,
        Ordering: sortSquadOrdering,
        roles: [
          {
            role: "string",
          },
        ],
        status: [
          {
            status: "string",
          },
        ],
        countries: [
          {
            countryId: "string",
          },
        ],
      };
      const response: ApiResponse<any[]> = await postData(apiUrls["GetSquadPlayerCardDetail"], requestParameters);
      handleApiResponse(
        response,
        (playerData) => {
          setSquadPlayersData(playerData);
          filterPlayers(roles, region, status);
          setIsSquadDataLoading(false);
        },
        (error) => {
          setIsSquadDataLoading(false);
          console.error("Error fetching Home Team Data:", error);
        }
      );
    };
    fetchTeamSquadPlayerCardData();
  }, [teamId, sortSquadOrderName, sortSquadOrdering]);

  const handleSelectionRegionChange = async (selectedOptions: SelectedOption[]) => {
    startLoading();
    setRegion((prevRegion) => {
      if (selectedOptions.length > 0) {
        const rolesByRegion: Record<string, SelectedOption[]> = {};
        prevRegion.forEach((area) => {
          if (!rolesByRegion[area.region]) {
            rolesByRegion[area.region] = [];
          }
          rolesByRegion[area.region].push(area);
        });
        selectedOptions.forEach((newOption) => {
          const existingOptions = rolesByRegion[newOption.region];
          if (existingOptions) {
            existingOptions.forEach((existingOption) => {
              const index = prevRegion.findIndex((area) => area.optionId === existingOption.optionId);
              if (index !== -1) {
                prevRegion.splice(index, 1);
              }
            });
          }
        });
        const updatedRoles = [...prevRegion, ...selectedOptions];
        return updatedRoles;
      } else {
        return [];
      }
    });
    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for state to update
    stopLoading();
  };

  const handleSortChange = () => {
    setIsSortClicked(false);
  };

  const sortByPositionData = ["Age", "Appearance", "Archetype", "Contract Left", "Position", "System Fit", "Wages PW"];

  const filterPlayers = (selectedRoles: string[], selectedRegions: SelectedOption[], selectedStatus: string[]) => {
    let filteredData = squadPlayersData;
    // if (selectedRoles.length > 0) {
    //   filteredData = filteredData.filter((player) =>
    //     selectedRoles.some((role) => {
    //       switch (role) {
    //         case "GK":
    //           return player.playingPosition === "Goalkeeper";
    //         case "Defence":
    //           return player.playingPosition === "Defender";
    //         case "Midfielder":
    //           return player.playingPosition === "Midfielder";
    //         case "Attack":
    //           return player.playingPosition === "Forward";
    //         default:
    //           return [];
    //       }
    //     })
    //   );
    // }
    if (selectedStatus.length > 0) {
      filteredData = filteredData.filter((player) =>
        selectedStatus.some((status) => {
          switch (status) {
            case "Currently Fit":
              return player.isInjured === false;
            case "Injured":
              return player.isInjured === true;
            case "Loaned In":
              return player.onLoan === true;
            case "Loaned Out":
              return player.givenOnLoan === true;
          }
        })
      );
    }
    if (selectedRegions.length > 0) {
      filteredData = filteredData.filter((player) => selectedRegions.some((region) => region.optionId === player.birthCountryId));
    }
    setFilteredPlayersData(filteredData);
  };

  useEffect(() => {
    filterPlayers(roles, region, status);
  }, [roles, region, status, squadPlayersData]);

  const handlePlayerComparision = () => {
    localStorage.removeItem("removedIds");
    const playerIds = filteredPlayersData.map((player) => player.playerId);
    navigate(`/playercomparison`, { state: { playerIds, isFromSortList: false, benchMarkPlayerId: null } });
  };

  return (
    <div className="common-squad-header">
      <StyledHeader className="squad-header-content">
        <LabelText className="player-family-name">Current squad</LabelText>
        <div className="squad-right-header">
          <SecondaryButton
            iconType="sort"
            label={
              <>
                <span className="sort-label">Sort by: </span>
                <LabelText className="clear-labeltext">{sortSquadOrderName}</LabelText>
              </>
            }
            onClick={() => setIsSortClicked((prev) => !prev)}
          />
          <SecondaryButton
            iconType="playercomparison"
            label="Comparison tools"
            className="comparision-tools-btn"
            onClick={handlePlayerComparision}
          />
        </div>
        {isSortClicked && (
          <SortDropdown ref={filterSortRef} className="newplayer-brop">
            <FilterSort onSortChange={handleSortChange} data={sortByPositionData} sortType={"Squad"} />
          </SortDropdown>
        )}
      </StyledHeader>
      <div className="common-squad-tab-content">
        {/* <FilterSectionStyles className="filters-section">
          <StyledFiltersData>
            <div className="roles">
              <LabelText className="player-search-text">Roles</LabelText>
              <MultiOptionCheckbox
                options={["GK", "Defence", "Midfielder", "Attack"]}
                type="roles"
                onSelectionChange={handleSelectionChange}
                includeAll={true}
                allOptionText="All"
                defaultSelectedOptions={["GK", "Defence", "Midfielder", "Attack"]}
              />
            </div>
            <div className="roles">
              <LabelText className="player-search-text">Status</LabelText>
              <MultiOptionCheckbox
                options={["Currently Fit", "Injured", "Loaned In", "Loaned Out"]}
                type="status"
                onStatusSelectionChange={handleStatusSelectionChange}
                includeAll={false}
                additionalText={[
                  numberOfFitPlayers.length,
                  numberOfInjuredPlayers.length,
                  numberOfLoanedInPlayers.length,
                  numberOfLoanedOutPlayers.length,
                ]}
              />
            </div>
            <div className="roles">
              <LabelText className="player-search-text">Region</LabelText>
              {regions?.map((regionItem) => (
                <FilteredDropdown
                  key={regionItem.region}
                  region={regionItem.region}
                  options={regionItem.areas}
                  type={regionItem.region}
                  onChange={handleSelectionRegionChange}
                  includeAll={true}
                  displayAsDropdown={true}
                />
              ))}
            </div>
          </StyledFiltersData>
        </FilterSectionStyles> */}
        <div className="squad-results-section tab-content-right">
          <SquadPlayerData roles={roles} players={filteredPlayersData || []} />
        </div>
      </div>
      {isSquadDataLoading && <LoadingSpinner />}
    </div>
  );
};

export default SquadPlayers;
